import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Trophy, ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import BenchmarkSparkline from './BenchmarkSparkline';

const fmtPct = (v, signed = false) => {
  const n = Number(v) || 0;
  const sign = signed ? (n > 0 ? '+' : n < 0 ? '−' : '') : '';
  return `${sign}${Math.abs(n).toFixed(2)}%`;
};

// Explainer for the MY PORTFOLIO tile: how many percentage points the
// portfolio beats / trails each ETF in the active benchmark set.
export default function BenchmarkBeatModal({ open, onClose, benchmarks = [], benchSet = 'dividend', info = {} }) {
  const mine = benchmarks.find((b) => !b.ticker) || null;
  const etfs = benchmarks.filter((b) => b.ticker);
  const myRet = Number(mine?.return_pct) || 0;
  const periodLabel = mine?.period_label || etfs[0]?.period_label || '';
  const beating = etfs.filter((b) => myRet - (Number(b.return_pct) || 0) > 0.005).length;
  const setLabel = benchSet === 'index' ? 'Index Benchmarks' : 'Dividend Benchmarks';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-lg sm:max-w-xl w-[95vw] sm:w-auto max-h-[88vh] overflow-y-auto pb-24 sm:pb-4"
        data-testid="benchmark-beat-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-start gap-2 pr-8 leading-snug">
            <Trophy className="w-5 h-5 mt-0.5 shrink-0 text-amber-500 dark:text-amber-400" />
            My Portfolio vs {setLabel}
          </DialogTitle>
          <DialogDescription>
            How your portfolio&apos;s price return over the selected window stacks up against each yardstick ETF. The gap is shown in <strong>percentage points</strong> — a plain subtraction of the two returns, not a ratio. A 10% portfolio return vs an 8% benchmark return is &ldquo;beating by 2.00 points&rdquo;.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border bg-muted/30 p-3 text-center" data-testid="benchmark-beat-formula">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">Formula</p>
          <p className="font-mono text-xs sm:text-sm font-semibold leading-relaxed break-words">
            My Portfolio {periodLabel}
            <span className="text-rose-600 dark:text-rose-400"> − </span>
            Benchmark {periodLabel}
          </p>
          <p className="font-mono text-[11px] sm:text-xs text-muted-foreground mt-1">= Difference in percentage points</p>
        </div>

        <div
          className={`rounded-lg border-2 p-3 text-center ${myRet >= 0
            ? 'border-emerald-300/60 dark:border-emerald-700/60 bg-emerald-50/40 dark:bg-emerald-950/20'
            : 'border-rose-300/60 dark:border-rose-700/60 bg-rose-50/40 dark:bg-rose-950/20'}`}
          data-testid="benchmark-beat-headline"
        >
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-bold mb-1">My Portfolio · {periodLabel}</p>
          <p className={`font-mono text-2xl font-bold ${myRet >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
            {myRet >= 0 ? <ArrowUpRight className="inline w-5 h-5 mb-1 mr-1" /> : <ArrowDownRight className="inline w-5 h-5 mb-1 mr-1" />}
            {fmtPct(myRet, true)}
          </p>
          <p className="text-[11px] text-muted-foreground mt-1" data-testid="benchmark-beat-scorecard">
            {etfs.length ? `Beating ${beating} of ${etfs.length} ${setLabel.toLowerCase()} this window.` : 'No benchmarks loaded.'}
          </p>
        </div>

        <div className="space-y-2">
          {etfs.map((b) => {
            const bRet = Number(b.return_pct) || 0;
            const diff = myRet - bRet;
            const beat = diff > 0.005;
            const trail = diff < -0.005;
            const tone = beat
              ? 'text-emerald-700 dark:text-emerald-400 border-emerald-300/60 dark:border-emerald-800/60 bg-emerald-50/50 dark:bg-emerald-950/25'
              : trail
                ? 'text-rose-700 dark:text-rose-400 border-rose-300/60 dark:border-rose-800/60 bg-rose-50/50 dark:bg-rose-950/25'
                : 'text-zinc-600 dark:text-zinc-300 border-zinc-300/60 dark:border-zinc-700 bg-muted/30';
            const Icon = beat ? ArrowUpRight : trail ? ArrowDownRight : Minus;
            const verdict = beat
              ? `Beating ${b.ticker} by ${Math.abs(diff).toFixed(2)} percentage points`
              : trail
                ? `Trailing ${b.ticker} by ${Math.abs(diff).toFixed(2)} percentage points`
                : `Matching ${b.ticker} this window`;
            return (
              <div key={b.ticker} className="rounded-md border bg-background/50 p-2.5" data-testid={`benchmark-beat-row-${b.ticker}`}>
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md border ${tone}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <span className="text-sm font-bold leading-snug min-w-0">{info[b.ticker]?.term || b.ticker}</span>
                      <span className={`font-mono text-sm font-bold shrink-0 whitespace-nowrap ${beat ? 'text-emerald-700 dark:text-emerald-400' : trail ? 'text-rose-600 dark:text-rose-400' : 'text-zinc-600 dark:text-zinc-300'}`} data-testid={`benchmark-beat-diff-${b.ticker}`}>
                        {diff > 0 ? '+' : diff < 0 ? '−' : ''}{Math.abs(diff).toFixed(2)} pts
                      </span>
                    </div>
                    <p className={`text-xs font-semibold mt-0.5 ${beat ? 'text-emerald-700 dark:text-emerald-400' : trail ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'}`} data-testid={`benchmark-beat-verdict-${b.ticker}`}>
                      {verdict}
                    </p>
                    <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
                      {fmtPct(myRet, true)} mine − {fmtPct(bRet, true)} {b.ticker} = {diff > 0 ? '+' : diff < 0 ? '−' : ''}{Math.abs(diff).toFixed(2)} pts
                    </p>
                    {info[b.ticker]?.def && (
                      <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{info[b.ticker].def}</p>
                    )}
                  </div>
                  <div className="w-20 shrink-0 self-center hidden sm:block">
                    <BenchmarkSparkline series={b.series} positive={bRet >= 0} testId={`benchmark-beat-spark-${b.ticker}`} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <p className="text-[10px] text-muted-foreground text-center leading-snug">
          Price-only returns on both sides (dividends excluded) over the same {periodLabel || 'window'}, so the comparison is apples-to-apples. Switch the period or benchmark set on the card and reopen to compare a different window.
        </p>
      </DialogContent>
    </Dialog>
  );
}
